import { useState } from 'react'
import { UserPlus } from 'lucide-react'
import type { StaffInvite, StaffMember } from '@manaro/contracts'
import { AddStaffDialog } from '@/features/staff/components/add-staff-dialog.tsx'
import { ConfirmStaffDialog } from '@/features/staff/components/confirm-staff-dialog.tsx'
import { InviteLinkDialog } from '@/features/staff/components/invite-link-dialog.tsx'
import { StaffTable } from '@/features/staff/components/staff-table.tsx'
import { useReissueInvite, useSetStaffEnabled, useStaff } from '@/features/staff/use-staff.ts'
import { ApiError } from '@/shared/api/client.ts'
import { Button } from '@/shared/ui/button.tsx'
import { Card } from '@/shared/ui/card.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'

type Dialog =
  | { kind: 'add' }
  | { kind: 'link'; invite: StaffInvite }
  | { kind: 'reissue'; member: StaffMember }
  | { kind: 'disable'; member: StaffMember }

export function StaffPage() {
  const list = useStaff()
  const reissue = useReissueInvite()
  const setEnabled = useSetStaffEnabled()
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const [enableError, setEnableError] = useState<string | null>(null)

  const close = () => {
    reissue.reset()
    setEnabled.reset()
    setDialog(null)
  }

  const showLink = (invite: StaffInvite) => setDialog({ kind: 'link', invite })

  const enable = (member: StaffMember) => {
    setEnableError(null)
    setEnabled.mutate(
      { id: member.id, enabled: true },
      {
        onError: (error) =>
          setEnableError(error instanceof ApiError ? error.message : 'Не получилось включить — попробуйте ещё раз.'),
      },
    )
  }

  const members = list.data?.members ?? []

  return (
    <>
      <header className="mb-6 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[300px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Сотрудники
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Доступы для поддержки: чаты, история обращений, салоны и обновления. Выручка, рефералы и этот раздел
            видны только владельцам сервиса.
          </p>
        </div>
        <Button
          size="sm"
          icon={<UserPlus className="size-3.5" />}
          disabled={list.data?.available === false}
          onClick={() => setDialog({ kind: 'add' })}
          className="max-md:min-h-11"
        >
          Добавить сотрудника
        </Button>
      </header>

      {enableError && <Alert className="mb-4">{enableError}</Alert>}

      {list.data && !list.data.available ? (
        <Alert>
          Сотрудников негде хранить: на сервере не подключена база чата поддержки (<code>SUPPORT_CHAT_DATABASE_URL</code>).
        </Alert>
      ) : (
        <Card className="overflow-hidden">
          {list.isPending ? (
            <div className="flex flex-col gap-3 p-5">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-5 w-2/5" />
            </div>
          ) : list.isError ? (
            <ErrorState
              message={list.error instanceof ApiError ? list.error.message : 'Не удалось загрузить сотрудников.'}
              onRetry={() => list.refetch()}
            />
          ) : members.length === 0 ? (
            <EmptyState
              title="Пока никого"
              description="Добавьте сотрудника: вы получите ссылку, по которой он сам задаст пароль."
            />
          ) : (
            <StaffTable
              members={members}
              onReissue={(member) => setDialog({ kind: 'reissue', member })}
              onDisable={(member) => setDialog({ kind: 'disable', member })}
              onEnable={enable}
            />
          )}
        </Card>
      )}

      {dialog?.kind === 'add' && <AddStaffDialog onCreated={showLink} onClose={close} />}
      {dialog?.kind === 'link' && <InviteLinkDialog invite={dialog.invite} onClose={close} />}
      {dialog?.kind === 'reissue' && (
        <ConfirmStaffDialog
          title="Выдать новую ссылку?"
          description={`${dialog.member.name} (${dialog.member.email}): ${
            dialog.member.status === 'active'
              ? 'старый пароль перестанет работать, и сотрудник выйдет из бэк-офиса, пока не задаст новый по ссылке.'
              : 'прежняя ссылка перестанет работать.'
          }`}
          confirmLabel="Выдать ссылку"
          pending={reissue.isPending}
          error={reissue.error}
          onConfirm={() => reissue.mutate(dialog.member.id, { onSuccess: showLink })}
          onClose={close}
        />
      )}
      {dialog?.kind === 'disable' && (
        <ConfirmStaffDialog
          title="Отключить доступ?"
          description={`${dialog.member.name} (${dialog.member.email}) сразу выйдет из бэк-офиса и не сможет войти. Включить обратно можно в любой момент.`}
          confirmLabel="Отключить"
          pending={setEnabled.isPending}
          error={setEnabled.error}
          onConfirm={() => setEnabled.mutate({ id: dialog.member.id, enabled: false }, { onSuccess: close })}
          onClose={close}
        />
      )}
    </>
  )
}
